import type { JSX } from 'react'
import { Link, useParams } from 'react-router'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { formatDateTime } from '@/lib/format'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { IST, STAGE_LABELS, STAGE_TONES, formatScore } from './stages'

type FollowUp = Schema<'FollowUpOut'>

/** `FOLLOW_UP_STATES` in `app/db/models/tenant.py`, in the tenant's words. */
const FOLLOW_UP_STATES: Record<string, { label: string; tone: string }> = {
  pending: { label: 'Scheduled', tone: 'info' },
  sent: { label: 'Sent', tone: 'success' },
  skipped: { label: 'Waiting', tone: 'neutral' },
  cancelled: { label: 'Stopped', tone: 'neutral' },
  failed: { label: 'Could not send', tone: 'danger' },
}

/** `FOLLOW_UP_CANCEL_REASONS`, said the way a tenant would say it. */
const CANCEL_REASONS: Record<string, string> = {
  replied: 'they replied',
  suppressed: 'they asked us to stop',
  won: 'you marked them won',
  lost: 'you marked them lost',
  ladder_changed: 'you changed the ladder',
  sending_paused: 'you switched sending off',
  no_address: 'we have no address for them on that channel',
  invalid_address: 'their address cannot be delivered to — check it and re-enter it',
}

/**
 * One lead — `design/screens/outreach.html#lead`, audit row §B2.
 *
 * The prototype's own argument for this screen is *"the score is explained by
 * its components. 'Why this lead' is what makes an automated list trustworthy —
 * without it people assume the ranking is arbitrary and ignore it."*
 *
 * WHAT THE SCORE PANEL SHOWS, AND WHY IT IS NOT THE PROTOTYPE'S FIVE ROWS
 * ------------------------------------------------------------------------
 * The prototype draws a component breakdown — *Title match +34*, *Company size
 * 500+ +22* — and there is no such thing anywhere in the product. `leads` carries
 * `relevance_score` and `relevance_reason`: one number and the model's own
 * sentence about why (`app/outreach/ai.Relevance`, whose docstring says the
 * reason is kept precisely because *"why was this a lead is the first question
 * anybody asks about a bad one"*).
 *
 * Rendering five invented components would be exactly the failure the prototype
 * is guarding against — a breakdown that looks measured and is fabricated. So
 * this shows the score and the model's reason, which is the true version of the
 * same promise, and says which model produced it.
 *
 * **The ICP sentence is what it was measured against**, and the screen says so
 * with a link: that is the loop `#compose`'s own help text describes, and until
 * S-B20 nothing on either end of it existed.
 */
export function LeadDetail(): JSX.Element {
  const { leadId = '' } = useParams()
  const { role } = useActiveTenant()
  const manageable = may(role, 'outreach:manage')
  const toast = useToast()

  const detail = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/outreach/leads/{lead_id}', {
          params: { path: { lead_id: leadId } },
          signal,
        }),
      ),
    [leadId],
  )

  const move = useMutation((stage: string) =>
    call(() =>
      api.POST('/api/v1/outreach/leads/{lead_id}/stage', {
        params: { path: { lead_id: leadId } },
        body: { stage },
      }),
    ),
  )

  if (detail.state.status === 'loading') return <Loading label="Loading this lead" lines={8} />
  if (detail.state.status === 'error')
    return (
      <div className="ar-stack ar-stack--loose">
        <h1 className="ar-h1">Lead</h1>
        <ErrorState
          failure={detail.state.failure}
          onRetry={detail.refetch}
          title="Could not load this lead"
        />
      </div>
    )

  const data = detail.state.data
  const { lead, contacts, follow_ups: followUps } = data

  const onMove = (stage: string): void => {
    void (async () => {
      const result = await move.run(stage)
      if (result.ok) {
        toast.show({
          kind: 'success',
          title: `Moved to ${STAGE_LABELS[stage] ?? stage}`,
          body:
            result.data.follow_ups_cancelled > 0
              ? 'Their follow-ups have stopped.'
              : 'Nothing was scheduled for them.',
        })
        detail.refetch()
      }
    })()
  }

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row">
        <Link className="ar-link" to="/outreach/leads">
          ← Leads
        </Link>
      </div>

      <div className="app-lead">
        <div className="ar-stack">
          <div className="ar-card">
            <div className="ar-card__header">
              <div>
                <h1 className="ar-h1">{lead.full_name ?? 'Unnamed lead'}</h1>
                <div className="ar-subtle app-lead__sub">
                  {[lead.headline, lead.company].filter(Boolean).join(' · ') || 'No title recorded'}
                </div>
              </div>
              <span className="ar-card__spacer" />
              <span className={`ar-badge ar-badge--${STAGE_TONES[lead.stage] ?? 'neutral'}`}>
                {STAGE_LABELS[lead.stage] ?? lead.stage}
              </span>
            </div>
            <div className="ar-card__body ar-stack">
              {contacts.length === 0 ? (
                <Empty
                  icon="○"
                  title="No address for this lead"
                  body="Their post carried no email address or phone number, so nothing can be sent to them. They stay in the list because the post itself may still be worth reading."
                />
              ) : (
                <div className="app-lead__contacts">
                  {contacts.map((contact: Schema<'ContactOut'>) => (
                    <div key={contact.id}>
                      <div className="ar-muted app-lead__label">{contact.kind}</div>
                      <div className="ar-mono app-lead__value">{contact.value}</div>
                      <div className="ar-subtle app-lead__meta">
                        {contact.first_contacted_at === null
                          ? 'Never contacted'
                          : `First contacted ${formatDateTime(contact.first_contacted_at ?? '', IST)}`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <span className="ar-field__help">
                Every address here came out of the prospect&rsquo;s own post, where they published
                it themselves. AugRank does not guess addresses and does not buy lists.
              </span>
            </div>
          </div>

          <div className="ar-card">
            <div className="ar-card__header">
              <h3>The post we found them in</h3>
              <span className="ar-card__spacer" />
              {data.keyword === null ? null : (
                <span className="ar-badge ar-badge--neutral">{data.keyword}</span>
              )}
            </div>
            <div className="ar-card__body ar-stack ar-stack--tight">
              {data.post_text === null ? (
                <Empty
                  icon="○"
                  title="No post text was kept"
                  body="This lead was added by hand or by an import rather than found in a search, so there is no post behind it."
                />
              ) : (
                <div className="ar-panel">
                  <p className="app-lead__post">{data.post_text}</p>
                </div>
              )}
              {data.post_url === null ? null : (
                <a
                  className="ar-link"
                  href={data.post_url}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Open on LinkedIn ↗
                </a>
              )}
            </div>
          </div>

          <div className="ar-card">
            <div className="ar-card__header">
              <h3>Follow-ups</h3>
              <span className="ar-card__spacer" />
              <Link className="ar-link app-lead__meta" to="/outreach/compose">
                Edit the ladder
              </Link>
            </div>
            <FollowUpList rows={followUps} />
          </div>
        </div>

        <div className="ar-stack">
          <div className="ar-card">
            <div className="ar-card__header">
              <h3>Score {formatScore(lead.relevance_score ?? null)}</h3>
            </div>
            <div className="ar-card__body ar-stack ar-stack--tight">
              {lead.relevance_reason === null ? (
                <p className="ar-subtle app-lead__meta">
                  Nobody has scored this lead. That happens when it was added by hand, or when the
                  model could not be reached during the search that found it — the lead is kept
                  either way, which is the direction AugRank fails in on purpose.
                </p>
              ) : (
                <p className="app-lead__reason">{lead.relevance_reason}</p>
              )}
              <hr className="app-lead__rule" />
              <span className="ar-subtle app-lead__meta">
                Scored by Haiku 4.5 against the sentence in{' '}
                <Link className="ar-link" to="/outreach/compose">
                  what we say
                </Link>
                . Change that sentence and later searches are judged against the new one.
              </span>
            </div>
          </div>

          <div className="ar-card">
            <div className="ar-card__body ar-stack">
              <InlineError failure={move.state.failure} />
              {data.conversation_id === null ? (
                <span className="ar-subtle app-lead__meta">
                  There is no conversation with this person yet. One opens the first time a message
                  is sent to them or arrives from them.
                </span>
              ) : (
                <Link className="ar-btn ar-btn--primary ar-btn--block" to="/outreach/inbox">
                  Open conversation
                </Link>
              )}
              {manageable ? (
                <>
                  <Button
                    block
                    busy={move.pending}
                    disabled={lead.stage === 'won'}
                    onClick={() => {
                      onMove('won')
                    }}
                  >
                    Move to Won
                  </Button>
                  <Button
                    block
                    tone="quiet"
                    busy={move.pending}
                    disabled={lead.stage === 'lost'}
                    onClick={() => {
                      onMove('lost')
                    }}
                  >
                    Mark as lost
                  </Button>
                </>
              ) : (
                <span className="ar-subtle app-lead__meta">
                  Only owners and admins can move a lead through the pipeline.
                </span>
              )}
              <Link className="ar-link app-lead__meta" to="/outreach/suppression">
                Add to the suppression list
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * The ladder as it happened to this person.
 *
 * Ordered newest-first by the API, which is the order an activity strip is read
 * in. A cancelled rung says *why* rather than only that it stopped — that is the
 * question a tenant asks about a prospect who went quiet, and `cancelled_reason`
 * is the column that can answer it.
 */
function FollowUpList({ rows }: { rows: FollowUp[] }): JSX.Element {
  if (rows.length === 0)
    return (
      <div className="ar-card__body">
        <Empty
          icon="○"
          title="Nothing scheduled"
          body="This lead is not in the follow-up ladder. Pressing Save & start sending on the What we say screen queues a first touch for everybody who has never been written to."
        />
      </div>
    )
  return (
    <div className="ar-card__body ar-stack ar-stack--tight">
      {rows.map((row) => {
        const state = FOLLOW_UP_STATES[row.state] ?? { label: row.state, tone: 'neutral' }
        return (
          <div className="ar-row app-lead__step" key={row.id}>
            <span className={`ar-badge ar-badge--${state.tone}`}>{state.label}</span>
            <span className="app-lead__meta">
              Step {row.step_no} · {row.channel}
            </span>
            <span className="ar-card__spacer" />
            <span className="ar-subtle app-lead__meta">
              {(row.sent_at ?? null) === null
                ? `due ${formatDateTime(row.due_at, IST)}`
                : formatDateTime(row.sent_at ?? row.due_at, IST)}
            </span>
          </div>
        )
      })}
      {rows.map((row) => {
        const why = row.cancelled_reason ?? null
        const error = row.last_error ?? null
        if (why === null && error === null) return null
        return (
          <span className="ar-subtle app-lead__meta" key={`${row.id}-why`}>
            Step {row.step_no}:{' '}
            {why === null ? error : `stopped because ${CANCEL_REASONS[why] ?? why}`}
          </span>
        )
      })}
    </div>
  )
}
